import React from "react";
import type { TFunction } from "i18next";
import type { Align, Row, Column } from "react-table";
import { IconButton } from "@material-ui/core";
import { faEllipsisV } from "@fortawesome/pro-light-svg-icons";
import type { IProfile } from "App/Modules/Models";
import { Permission, SiteState } from "App/Modules/Models";
import {
    LargeNumber,
    sortReactTableColumnWithNullLast,
    formatDateTime,
    humanizeDateTime,
    StyledDateTime,
    sortReactTableMomentColumnWithNullLast,
    faIcon,
    hasUserAccessThroughSupportingPartner,
} from "App/ReactComponents/Shared";
import type { CombinedSite } from "../../Models";
import { siteAvatar, SiteCell, SiteIssuesSummaryCell } from "./SitesListHelpers";

export const getSitesListUIColumns = ({
    handleSiteMenuClicked,
    showSiteInformation,
    showMonitoring,
    t,
    profile,
}: {
    handleSiteMenuClicked: (site: CombinedSite, event: React.SyntheticEvent<any>) => void;
    showSiteInformation: boolean;
    showMonitoring: boolean;
    t: TFunction;
    profile: IProfile;
}) =>
    [
        {
            id: "avatar",
            Cell: (cell) => siteAvatar(cell.row.original),
            width: 58,
            maxWidth: 58,
            minWidth: 58,
            disableGlobalFilter: true,
        },
        {
            Header: t("Entities.Site.Site"),
            accessor: "name",
            Cell: (cell) => (
                <SiteCell
                    site={cell.row.original}
                    linkToSite={
                        // temporary fix for disabling link to sites user obviously doesn't have access to
                        (cell.row.original.siteState === SiteState.Ended || cell.row.original.numCameras > -1) &&
                        hasUserAccessThroughSupportingPartner(
                            profile,
                            cell.row.original.partner,
                            Permission.SiteInformation,
                        )
                    }
                />
            ),
            minWidth: 150,
            width: 300,
        },
        {
            Header: t("Entities.Partner.Partner"),
            accessor: "partner.name" as any,
        },
        {
            Header: t("Entities.Company.Company"),
            accessor: "company.name" as any,
        },
        {
            Header: t("Entities.Label.Labels"),
            accessor: "labelNames",
            Cell: (cell) => cell.row.original.labelNames?.join(", "),
        },
        {
            Header: t("Entities.Site.SiteState"),
            id: "siteState",
            accessor: (row) => t(`Enums.SiteState.Enum${row.siteStateName}`),
        },
        {
            accessor: "siteStateIsEnded",
            Cell: (cell) => cell.row.original.siteStateIsEnded,
            disableGlobalFilter: true,
        },
        ...(showSiteInformation
            ? [
                  {
                      Header: t("Entities.Equipment.Cameras"),
                      accessor: "numCameras" as any,
                      align: "center" as Align,
                      width: 90,
                      maxWidth: 90,
                      Cell: (cell) => (
                          <LargeNumber disabled={!cell.row.original.numCameras}>
                              {cell.row.original.numCameras > -1 ? cell.row.original.numCameras : "-"}
                          </LargeNumber>
                      ),
                      sortDescFirst: true,
                  },
                  {
                      Header: t("Gui.Common.Labels.24h"),
                      accessor: "eventsLastDay",
                      align: "center" as Align,
                      width: 90,
                      maxWidth: 90,
                      Cell: (cell) => (
                          <LargeNumber color="primary" disabled={!cell.row.original.eventsLastDay}>
                              {cell.row.original.eventsLastDay > -1 ? cell.row.original.eventsLastDay : "-"}
                          </LargeNumber>
                      ),
                      sortDescFirst: true,
                  },
                  {
                      Header: t("Gui.Common.Labels.7d"),
                      accessor: "eventsLastWeek",
                      align: "center" as Align,
                      width: 90,
                      maxWidth: 90,
                      Cell: (cell) => (
                          <LargeNumber color="primaryDark" disabled={!cell.row.original.eventsLastWeek}>
                              {cell.row.original.eventsLastWeek > -1 ? cell.row.original.eventsLastWeek : "-"}
                          </LargeNumber>
                      ),
                      sortDescFirst: true,
                  },
              ]
            : []),
        ...(showMonitoring
            ? [
                  {
                      Header: t("Entities.Issue.Issues"),
                      id: "issuesSummary",
                      accessor: (row: CombinedSite) => {
                          const { numIssuesError, numIssuesWarning, numIssuesOther } = row;
                          return numIssuesError + numIssuesWarning + numIssuesOther || 0;
                      },
                      width: 100,
                      maxWidth: 100,
                      minWidth: 40,
                      Cell: (cell) => <SiteIssuesSummaryCell site={cell.row.original} />,
                      sortDescFirst: true,
                  },
                  {
                      Header: t("Entities.Issue.NotAcknowledged"),
                      accessor: "numIssuesNew",
                      align: "center" as Align,
                      Cell: (cell) => (
                          <>
                              {(cell.row.original.numIssuesActive || cell.row.original.numIssuesNew) &&
                                  LargeNumber({
                                      children: cell.row.original.numIssuesNew,
                                      disabled: !cell.row.original.numIssuesNew,
                                  })}
                          </>
                      ),
                      sortType: (a, b, id, desc) =>
                          sortReactTableColumnWithNullLast<Row<CombinedSite>>((s) => s.original.numIssuesNew)(
                              a,
                              b,
                              desc ? "desc" : "asc",
                          ),
                      sortDescFirst: true,
                      width: 90,
                      maxWidth: 90,
                  },
                  {
                      Header: t("Entities.Issue.NotResolved"),
                      accessor: "numIssuesActive" as any,
                      align: "center" as Align,
                      Cell: (cell) => (
                          <>
                              {(cell.row.original.numIssuesActive || cell.row.original.numIssuesNew) &&
                                  LargeNumber({
                                      children: cell.row.original.numIssuesActive,
                                      disabled: !cell.row.original.numIssuesActive,
                                  })}
                          </>
                      ),
                      sortType: (a, b, id, desc) =>
                          sortReactTableColumnWithNullLast<Row<CombinedSite>>((s) => s.original.numIssuesActive)(
                              a,
                              b,
                              desc ? "desc" : "asc",
                          ),
                      sortDescFirst: true,
                      width: 90,
                      maxWidth: 90,
                  },
                  {
                      Header: t("Entities.Issue.LatestIssue"),
                      id: "dtLatestIssue",
                      accessor: (row) =>
                          `${formatDateTime(row.dtLatestIssue, row.timeZone)} ${humanizeDateTime(
                              row.dtLatestIssue,
                          )} ${t("Gui.Common.Labels.Ago")}`,
                      Cell: (cell) => (
                          <StyledDateTime
                              dateTime={cell.row.original.dtLatestIssue}
                              timeZone={cell.row.original.timeZone}
                          />
                      ),
                      sortType: (a, b, id, desc) =>
                          sortReactTableMomentColumnWithNullLast<Row<CombinedSite>>((s) => s.original.dtLatestIssue)(
                              a,
                              b,
                              desc ? "asc" : "desc",
                          ),
                      sortInverted: true,
                      width: 150,
                      maxWidth: 150,
                  },
              ]
            : []),
        {
            // Used for filtering only, fuzzy filter will ensure exact matches only
            accessor: "id",
        },
        {
            id: "actionButtons",
            align: "center",
            disableSortBy: true,
            disableGlobalFilter: true,
            disableGroupBy: true,
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            Cell: (cell) => (
                <IconButton onClick={(event) => handleSiteMenuClicked(cell.row.original, event)}>
                    {faIcon(faEllipsisV)}
                </IconButton>
            ),
        },
    ] as Column<CombinedSite>[];
